import React, { useEffect, useRef, useState } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom'; // Uncomment if navigation is needed

// Firebase Imports (ensure '../firebase.js' is configured)
import { db, Storage, auth } from '../firebase'; // Adjust path as needed
import { ref as storageRef, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';
import { setDoc, doc, collection, serverTimestamp, addDoc } from 'firebase/firestore';

// UI Imports
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StickerEditor = () => {
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  // const navigate = useNavigate(); // For redirection after save

  // UI State
  const [loading, setLoading] = useState(false); // General loading for file processing
  const [isSaving, setIsSaving] = useState(false); // Specific for product save operation
  const [librariesLoaded, setLibrariesLoaded] = useState(false);

  // Content State
  const [currentFile, setCurrentFile] = useState(null); // Original uploaded file (PDF or image)
  const [contentImageUrl, setContentImageUrl] = useState(null); // Data URL for canvas display
  const [mockupImageUrl, setMockupImageUrl] = useState(null); // For local UI display if needed
  const [modifications, setModifications] = useState([]);

  // Product Information
  const [productName, setProductName] = useState('');
  const productNameRef = useRef(''); // To capture product name at time of save

  // Pricing state
  const [baseCost, setBaseCost] = useState(0);
  const [printCost, setPrintCost] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  const [currentImageSize, setCurrentImageSize] = useState({ width: 0, height: 0 }); // Actual design dimensions in inches

  const stickerConfig = {
    width: 2 * 144, height: 2 * 144, rx: 25, ry: 25, label: '2" x 2" Rounded Sticker',
    basePrice: 3, printPrice: 0, printAreaPaddingFactor: 0.07, dpi: 144,
  };

  useEffect(() => {
    productNameRef.current = productName;
  }, [productName]);

  const calculateBaseCost = () => stickerConfig.basePrice;
  const calculatePrintCost = (hasDesign) => hasDesign ? stickerConfig.printPrice : 0;
  const calculateTotalPrice = (base, print) => base + print;

  useEffect(() => {
    const newBaseCost = calculateBaseCost();
    const hasContent = !!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false);
    const newPrintCost = calculatePrintCost(hasContent);
    const newTotalPrice = calculateTotalPrice(newBaseCost, newPrintCost);
    setBaseCost(newBaseCost); setPrintCost(newPrintCost); setTotalPrice(newTotalPrice);
  }, [contentImageUrl, modifications, librariesLoaded]);

  const updateMockupImageForDisplay = () => { // For UI Mockup state
    if (!fabricCanvasRef.current) return;
    const dataURL = fabricCanvasRef.current.toDataURL({ format: 'png', quality: 0.9, multiplier: 1 });
    setMockupImageUrl(dataURL);
  };

  const updateImageSizeInfo = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const contentImage = canvas.getObjects().find(obj => obj.isContent && (obj.isUploadedImage || obj.type === 'image'));
    if (contentImage) {
      const scaledWidthPx = contentImage.getScaledWidth();
      const scaledHeightPx = contentImage.getScaledHeight();
      const widthInches = (scaledWidthPx / stickerConfig.dpi).toFixed(2);
      const heightInches = (scaledHeightPx / stickerConfig.dpi).toFixed(2);
      setCurrentImageSize({ width: parseFloat(widthInches), height: parseFloat(heightInches) });
    } else {
      setCurrentImageSize({ width: 0, height: 0 });
    }
  };

  // Library Loading
  useEffect(() => {
    const loadScript = (src) => new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
      const script = document.createElement('script');
      script.src = src; script.async = true; script.onload = resolve; script.onerror = reject;
      document.head.appendChild(script);
    });
    const loadLibraries = async () => {
      setLoading(true);
      try {
        if (!window.pdfjsLib) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js');
          if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          else throw new Error("pdf.js failed to load.");
        }
        if (!window.fabric) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.0/fabric.min.js');
          if (!window.fabric) throw new Error("Fabric.js failed to load.");
        }
        setLibrariesLoaded(true);
      } catch (error) {
        console.error('Error loading libraries:', error);
        toast.error(`Libs load error: ${error.message}. Refresh.`);
      } finally { setLoading(false); }
    };
    loadLibraries();
  }, []);

  // Canvas Initialization & Event Handlers
  useEffect(() => {
    if (!librariesLoaded || !canvasRef.current || fabricCanvasRef.current) return;
    const canvas = new window.fabric.Canvas(canvasRef.current);
    fabricCanvasRef.current = canvas;
    setupStickerCanvas(canvas, stickerConfig);

    const updateMods = (obj) => {
      const mod = { id: obj.id || `stk_obj_${Date.now()}`, type: obj.type, left: obj.left, top: obj.top, scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle, ...(obj.type === 'i-text' && { text: obj.text, fontFamily: obj.fontFamily, fill: obj.fill, fontSize: obj.fontSize })};
      if (!obj.id) obj.set('id', mod.id);
      setModifications(prev => {
        const idx = prev.findIndex(m => m.id === mod.id);
        if (idx > -1) { const upd = [...prev]; upd[idx] = mod; return upd; }
        return [...prev, mod];
      });
    };

    const commonEventHandler = (e) => {
      if (e.target && e.target.isContent) updateMods(e.target);
      setTimeout(() => { updateMockupImageForDisplay(); updateImageSizeInfo(); }, 50);
    };
    canvas.on({ 'object:modified': commonEventHandler, 'object:added': commonEventHandler, 'object:removed': (e) => {
      if (e.target && e.target.isContent && e.target.id) setModifications(prev => prev.filter(m => m.id !== e.target.id));
      setTimeout(() => { updateMockupImageForDisplay(); updateImageSizeInfo(); }, 50);
    }, 'object:scaling': updateImageSizeInfo, 'path:created': commonEventHandler });
    
    window.dispatchEvent(new Event('resize'));
    return () => { fabricCanvasRef.current?.dispose(); fabricCanvasRef.current = null; };
  }, [librariesLoaded]);

  const setupStickerCanvas = (canvas, config) => {
    if (!canvas || !config) return;
    canvas.setDimensions({ width: config.width, height: config.height });
    canvas.setBackgroundColor('transparent', canvas.renderAll.bind(canvas));
    let stickerShape = canvas.getObjects().find(obj => obj.isStickerShape);
    const shapeProps = { left: 0, top: 0, width: config.width, height: config.height, fill: 'white', rx: config.rx || 0, ry: config.ry || 0, selectable: false, evented: false, isStickerShape: true, id: 'sticker_base_shape' };
    if (!stickerShape) stickerShape = new window.fabric.Rect(shapeProps);
    else stickerShape.set(shapeProps);
    canvas.add(stickerShape).sendToBack(stickerShape);
    canvas.clipPath = stickerShape;
    canvas.getObjects().filter(o => o.isContent).forEach(obj => repositionContentOnSticker(obj, canvas, config));
    canvas.renderAll();
    setTimeout(updateMockupImageForDisplay, 100);
  };

  const repositionContentOnSticker = (obj, canvas, conf) => {
    if (!obj || !canvas || !conf) return;
    const padding = conf.width * (conf.printAreaPaddingFactor || 0.05);
    const areaW = conf.width - 2 * padding, areaH = conf.height - 2 * padding;
    const origW = obj.get('originalWidth') || obj.width, origH = obj.get('originalHeight') || obj.height;
    let scale = 1;
    if (origW > 0 && origH > 0) scale = Math.min(areaW / origW, areaH / origH, 1);
    obj.set({ scaleX: scale, scaleY: scale, left: padding + (areaW - origW * scale) / 2, top: padding + (areaH - origH * scale) / 2, originX: 'left', originY: 'top' }).setCoords();
  };

  useEffect(() => { // Content Image Loading
    const canvas = fabricCanvasRef.current;
    if (!canvas || !contentImageUrl || !librariesLoaded) return;
    canvas.getObjects().filter(o => o.isContent && o.isUploadedImage).forEach(obj => canvas.remove(obj));
    window.fabric.Image.fromURL(contentImageUrl, (img) => {
      if (!img) { console.error("Sticker: Failed to load image:", contentImageUrl); return; }
      img.set({ originalWidth: img.width, originalHeight: img.height, isContent: true, isUploadedImage: true, selectable: true, id: `stk_img_${Date.now()}`, originX: 'left', originY: 'top' });
      canvas.add(img);
      repositionContentOnSticker(img, canvas, stickerConfig);
      canvas.setActiveObject(img).renderAll();
      setTimeout(() => { updateImageSizeInfo(); updateMockupImageForDisplay(); }, 100);
    }, { crossOrigin: 'anonymous' });
  }, [contentImageUrl, librariesLoaded]);

  const handleFileChange = async (e) => { // Adapts `handleFileUpload` concept part 1
    if (!librariesLoaded) { toast.warn('Libraries loading...'); return; }
    const file = e.target.files[0]; if (!file) return;
    setLoading(true); setCurrentFile(file); // Store original file
    try {
      let imgURL;
      if (file.type === 'application/pdf') { // Convert PDF to image
        if (!window.pdfjsLib) throw new Error('PDF library not available.');
        imgURL = await renderPDFToImageURL(file); // This is `convertPdfToImage`
      } else if (file.type.startsWith('image/')) {
        imgURL = await readFileToDataURL(file);
      } else { throw new Error('Unsupported file. Use PDF/image.'); }
      setContentImageUrl(imgURL); // Stores image URL for canvas rendering
      toast.success("File processed for preview.");
    } catch (err) {
      console.error('File processing error:', err);
      toast.error(`File load error: ${err.message}`);
    } finally {
      setLoading(false); if (e.target) e.target.value = null;
    }
  };

  const renderPDFToImageURL = async (file) => { // This is `convertPdfToImage`
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 3.0 }); // Higher scale for better quality from PDF
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = viewport.width; tempCanvas.height = viewport.height;
    const ctx = tempCanvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    return tempCanvas.toDataURL('image/png');
  };

  const readFileToDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject; reader.readAsDataURL(file);
  });

  const deleteSelected = () => {
    const c = fabricCanvasRef.current; if (!c) return;
    c.getActiveObjects().filter(o => o.isContent).forEach(obj => c.remove(obj));
    c.discardActiveObject().renderAll();
  };

  const clearCanvasContent = () => {
    const c = fabricCanvasRef.current; if (!c) return;
    c.getObjects().filter(obj => obj.isContent).forEach(obj => c.remove(obj));
    c.discardActiveObject().renderAll();
    setContentImageUrl(null); setModifications([]); setCurrentImageSize({ width: 0, height: 0 });
    setTimeout(updateMockupImageForDisplay, 50);
  };
  
  const addText = () => {
    const c = fabricCanvasRef.current; if (!c || !librariesLoaded) return;
    const text = new window.fabric.IText('Your Text', { fontFamily: 'Arial', fontSize: Math.max(12, Math.min(stickerConfig.width / 12, 36)), fill: '#333', textAlign: 'center', isContent: true, id: `stk_txt_${Date.now()}`, originX: 'left', originY: 'top' });
    c.add(text); repositionContentOnSticker(text, c, stickerConfig);
    c.setActiveObject(text).renderAll();
  };
  
  // Responsive Scaling
  useEffect(() => {
    const container = canvasContainerRef.current;
    const canvasEl = fabricCanvasRef.current?.wrapperEl;
    if (!container || !canvasEl || !librariesLoaded) return;
    const observer = new ResizeObserver(entries => {
      if (!fabricCanvasRef.current || !entries || !entries.length) return;
      const { width: contW } = entries[0].contentRect;
      const logW = fabricCanvasRef.current.getWidth();
      let scale = 1; if (logW > 0 && contW < logW) scale = contW / logW;
      canvasEl.style.transform = `scale(${scale})`;
      canvasEl.style.transformOrigin = 'top left';
      container.style.height = `${fabricCanvasRef.current.getHeight() * scale}px`;
    });
    observer.observe(container);
    const initW = container.clientWidth;
    if (fabricCanvasRef.current) {
      const logW = fabricCanvasRef.current.getWidth();
      let currentScale = 1; if (logW > 0 && initW < logW) currentScale = initW / logW;
      canvasEl.style.transform = `scale(${currentScale})`;
      container.style.height = `${fabricCanvasRef.current.getHeight() * currentScale}px`;
    }
    return () => observer.disconnect();
  }, [librariesLoaded]);


  // --- Firebase Save Logic ---

  // Upload original PDF (if provided) to Firebase Storage.
  const uploadOriginalPdfToStorage = async (pdfFile, designId, userId) => {
    if (!pdfFile || pdfFile.type !== 'application/pdf') return null;
    // Path: BrandingImages/Stickers/{userId}/{designId}_original.pdf
    const filePath = `BrandingImages/Stickers/${designId}_original.pdf`;
    const fileRef = storageRef(Storage, filePath);
    await uploadBytes(fileRef, pdfFile);
    return await getDownloadURL(fileRef);
  };

  // Function 3: saveCanvasToStorage (adapted for Stickers)
  const saveStickerMockupToStorage = async (canvasInstance, designId, userId) => {
    if (!canvasInstance) throw new Error("Canvas not available for mockup generation.");
    // Ensure the sticker shape is visible and content is clipped for an accurate mockup
    const dataURL = canvasInstance.toDataURL({ format: 'png', quality: 0.9, multiplier: 1.5 }); // Good quality mockup
    if (!dataURL) throw new Error("Failed to generate sticker mockup image data.");

    // Path: BrandingMockups/Stickers/{userId}/{designId}_mockup.png
    const filePath = `BrandingMockups/Stickers/${designId}_mockup.png`;
    const fileRef = storageRef(Storage, filePath);
    await uploadString(fileRef, dataURL, 'data_url');
    return await getDownloadURL(fileRef);
  };

  // Function 4: handleSaveProduct
  const handleSaveProduct = async () => {
    if (!auth.currentUser) {
      toast.error("Please log in to save your sticker design.");
      return;
    }
    if (!productName.trim()) {
      toast.error("Please enter a Sticker Name for your design.");
      return;
    }
    const hasContent = !!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false);
    if (!hasContent) {
      toast.error("Please add a design or text to your sticker before saving.");
      return;
    }

    setIsSaving(true);
    const savingToastId = toast.loading("Saving your sticker design...");

    const userId = auth.currentUser.uid;
    const designDocRef = doc(collection(db, "users", userId, "BrandingDesigns"));
    const designId = designDocRef.id;

    let originalPdfUrl = null;
    let mockupStickerUrl = null;

    try {
      // 1. Upload Original PDF (if currentFile is a PDF) - Part of `handleFileUpload` concept
      if (currentFile && currentFile.type === 'application/pdf') {
        toast.update(savingToastId, { render: "Uploading original PDF..." });
        originalPdfUrl = await uploadOriginalPdfToStorage(currentFile, designId, userId);
      }

      // 2. Generate and Upload Mockup from Canvas
      toast.update(savingToastId, { render: "Generating sticker mockup..." });
      if (!fabricCanvasRef.current) throw new Error("Canvas is not ready for mockup.");
      mockupStickerUrl = await saveStickerMockupToStorage(fabricCanvasRef.current, designId, userId);

      // 3. Prepare Firestore Payload
const payload = {
  designName: productName.trim(),
  totalPrice: totalPrice,

  frontDesign: originalPdfUrl, 

  size: stickerConfig.label, 
  color: null, 

  images: {
    front: mockupStickerUrl,
  },

  costs: {
    front: printCost,
  },

  Printsizes: {
    front:
      currentImageSize?.width > 0 && currentImageSize?.height > 0
        ? currentImageSize
        : null,
  },

  productName: "Sticker", // Or dynamic name if you use a product catalog
  SKU: `STCKR-${designId}`,
  baseCost: baseCost,
  PrintCost: printCost,
  timestamp: serverTimestamp(),
  userId: userId,

  // Optional: save full config if needed later
  // stickerConfiguration: { ...stickerConfig },
};

      console.log("Sticker design payload:", payload);
      // 4. Save to Firestore
      toast.update(savingToastId, { render: "Saving design details to database..." });
      await setDoc(designDocRef, payload);

      toast.update(savingToastId, { render: "Sticker design saved successfully!", type: "success", isLoading: false, autoClose: 5000 });
      // navigate('/my-designs'); // Optional: redirect user after successful save

    } catch (error) {
      console.error("Error saving sticker design:", error);
      toast.update(savingToastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 7000 });
    } finally {
      setIsSaving(false);
    }
  };

  // --- END Firebase Save Logic ---

  const ControlCard = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6"> {/* Added mb-6 for spacing if multiple cards */}
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <ToastContainer position="top-right" theme="colored" />
      <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-10">
          <div className="max-w-screen-2xl mx-auto px-6 py-1">
            <h1 className="text-2xl font-bold text-gray-900">Sticker Designer</h1>
          </div>
      </header>

      <main className="flex-grow grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 overflow-hidden">
        
        <aside className="col-span-12 lg:col-span-3 flex flex-col gap-0 overflow-y-auto pb-4"> {/* Reduced gap for denser packing of cards */}
            <ControlCard title="1. Upload Your Design">
              <input 
                type="file" 
                accept=".pdf,.png,.jpg,.jpeg" 
                onChange={handleFileChange} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" 
                disabled={!librariesLoaded || loading || isSaving}
              />
              {!librariesLoaded && <div className="mt-2 p-2 text-xs bg-yellow-100 text-yellow-800 rounded">Loading libraries...</div>}
              {loading && <div className="mt-2 p-2 text-xs bg-blue-100 text-blue-800 rounded">Processing file...</div>}
            </ControlCard>

            <div title="2. Sticker Details">
              <div className="space-y-4 pb-4">
                <div>
                  <label htmlFor="stickerNameInput" className="block text-sm font-medium text-gray-700 mb-1">Sticker Name</label>
                  <input
                    id="stickerNameInput" type="text" value={productName} onChange={(e) => setProductName(e.target.value)}
                    placeholder="e.g., Cool Logo Sticker"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    disabled={isSaving}
                  />
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sticker Size</label>
                  <input type="text" value={stickerConfig.label} readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed" />
                </div>
              </div>
            </div>

            <ControlCard title="Image Size Info">
              {currentImageSize.width > 0 ? (
                <div className="space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Design Size:</span><span className="font-medium text-gray-800">{currentImageSize.width}" × {currentImageSize.height}"</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Sticker Area:</span><span className="font-medium text-gray-800">{(stickerConfig.width / stickerConfig.dpi).toFixed(1)}" × {(stickerConfig.height / stickerConfig.dpi).toFixed(1)}"</span></div>
                </div>
              ) : ( <div className="text-sm text-gray-500 text-center py-2">No image loaded</div> )}
            </ControlCard>
        </aside>

        <section className="col-span-12 lg:col-span-5 flex flex-col bg-white rounded-lg shadow-inner border border-gray-200 overflow-hidden">
          <div ref={canvasContainerRef} className="flex-grow flex items-top pt-10 justify-center p-4 bg-gray-200 overflow-auto relative">
            {isSaving && <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20"><p className="text-blue-600 font-semibold">Saving sticker...</p></div>}
            <div className="relative w-min h-min shadow-md"> 
              <canvas ref={canvasRef} />
            </div>
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-4 flex flex-col gap-0 overflow-y-auto pb-4">
          <ControlCard title="Pricing Summary">
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Base Setup Cost:</span><span className="font-medium text-gray-800">₹{baseCost.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Print Cost (Design):</span><span className="font-medium text-gray-800">₹{printCost.toFixed(2)}</span></div>
                <hr className="my-2 border-dashed" />
                <div className="flex justify-between items-center text-lg font-bold text-blue-800"><span>Total Price:</span><span>₹{totalPrice.toFixed(2)}</span></div>
              </div>
          </ControlCard>
          
          <ControlCard title="Design Tools">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={addText} className="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 text-sm font-medium" disabled={!librariesLoaded || loading || isSaving}>Add Text</button>
              <button onClick={deleteSelected} className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 text-sm font-medium" disabled={!librariesLoaded || loading || isSaving}>Delete Selected</button>
              <button onClick={clearCanvasContent} className="col-span-2 px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 text-sm font-medium" disabled={!librariesLoaded || loading || isSaving}>Clear Design</button>
            </div>
          </ControlCard>
          
          {/* SAVE STICKER BUTTON */}
          <ControlCard title="Finalize & Save">
            <button
                onClick={handleSaveProduct}
                disabled={!librariesLoaded || isSaving || loading || !productName.trim() || !(!!contentImageUrl || (fabricCanvasRef.current?.getObjects().some(o => o.isContent) ?? false))}
                className="w-full px-4 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
                {isSaving ? 'Saving...' : 'Save Sticker Design'}
            </button>
            {!auth.currentUser && <p className="text-xs text-red-500 mt-2 text-center">Please log in to save.</p>}
          </ControlCard>
          
          {/* Optional Local Export Options - can be removed if not needed */}
          {/* 
          <ControlCard title="Local Export Options">
            <div className="space-y-2">
              <button onClick={exportAsImage} className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-50" disabled={!librariesLoaded || loading || isSaving}>Export PNG (300dpi)</button>
              <button onClick={downloadPDF} className="w-full px-4 py-2 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50" disabled={!librariesLoaded || loading || isSaving || !currentFile || currentFile.type !== 'application/pdf'}>Download Original PDF</button>
              <button onClick={copyJSONToClipboard} className="w-full px-4 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50" disabled={!librariesLoaded || loading || isSaving}>Copy JSON Data</button>
            </div>
          </ControlCard> 
          */}
        </aside>
      </main>
    </div>
  );
};

export default StickerEditor;